import DOMPurify from 'dompurify'
import parse from 'html-react-parser'
import moment from 'moment'
import type { ReactNode } from 'react'
import type {
  ClinicGivenItem,
  HospitalStayFacts,
  MedicationChange,
  MedicationChangeType,
  VisitAppointment,
  VisitFacts
} from '../../../api/patientSummary'
import SignatureBlock from '../SignatureBlock'
import { type DocumentLabelLanguage, documentLabel, documentLabelLanguage } from '../../../utils/patientDocumentLabels'
import { stripSuggestionHighlight } from '../../../utils/visitSummaryHtml'
import { isInpatientFacts, visitTests } from '../../../utils/visitSummaryVariant'
import { visitSummaryDocumentCss } from '../../../utils/visitSummaryPrint'

export type VisitSummaryDocumentProps = {
  facts: VisitFacts
  instructionsHtml: string
  translationHtml?: string | null
  languageName?: string | null
  /** The translation's language code: its fixed labels (utils/patientDocumentLabels) label the translated page.
   * Without one (or for a code the table lacks) the translated page keeps the English labels. */
  language?: string | null
  letterhead?: ReactNode
  /** Printed in the footer as "Questions? Call the clinic on …" — left out when unknown. */
  clinicPhone?: string | null
  /** "Note from your doctor": the doctor's own text, printed last before the signature; left out when blank. The
   * translated page needs no copy of it — the translation already ends with the translated note. */
  doctorNote?: string | null
}

// The only markup PatientSummaries::Renderer (and Translator) ever emit: plain structure plus the
// inline color/background-color styles used for the red warning heading and (until stripped) the
// AI-suggestion highlight. Nothing else is trusted, even though the server already sanitises this
// HTML — this is a second, independent allowlist.
const ALLOWED_TAGS = ['p', 'strong', 'em', 'ul', 'ol', 'li', 'br', 'span']
const ALLOWED_ATTR = ['style']

// The patient document must never show the yellow "AI-suggested" highlight (that's an editing-time
// affordance for the doctor) — strip it after sanitising, on top of the server-side strip on
// completion (PatientSummaries::SuggestionStripper), so this component can never regress that on its own.
const sanitizeForPatient = (html: string): string =>
  stripSuggestionHighlight(DOMPurify.sanitize(html ?? '', { ALLOWED_TAGS, ALLOWED_ATTR }))

const CHANGE_BADGE: Record<MedicationChangeType, string> = {
  new: 'NEW',
  changed: 'CHANGED',
  stopped: 'STOPPED',
  continued: '—'
}

// Medicine `route` values come from the clinic's own configured route list (already words like
// "Oral", "Topical", not codes like "PO") — this only spells out the common ones as a short plain
// phrase for the patient; anything unrecognised is shown as-is rather than dropped.
const ROUTE_PHRASES: Record<string, string> = {
  oral: 'by mouth',
  po: 'by mouth',
  iv: 'into a vein',
  intravenous: 'into a vein',
  im: 'as an injection into the muscle',
  intramuscular: 'as an injection into the muscle',
  sc: 'as an injection under the skin',
  subcutaneous: 'as an injection under the skin',
  topical: 'on the skin',
  sublingual: 'under the tongue',
  rectal: 'into the rectum',
  nasal: 'into the nose',
  ophthalmic: 'in the eye',
  otic: 'in the ear',
  inhalation: 'by inhaling'
}

const routeInPlainWords = (route: string | null): string | null => {
  if (!route?.trim()) return null
  const phrase = ROUTE_PHRASES[route.trim().toLowerCase()]
  return phrase ?? route.trim()
}

const medicineName = (med: MedicationChange): string => [med.name, med.strength].filter(Boolean).join(' ').trim()

// Deterministic, from facts only — never from the AI-drafted instructions HTML.
const howToTake = (med: MedicationChange): string => {
  if (med.change === 'stopped') return 'Do not take'
  const dose = [med.dose_quantity, med.dose_unit].filter(v => v !== null && v !== '').join(' ')
  const parts = [dose, med.frequency, routeInPlainWords(med.route)].filter((v): v is string => Boolean(v?.trim()))
  return parts.join(' ') || '—'
}

const durationText = (med: MedicationChange): string => {
  if (med.change === 'stopped') return ''
  if (!med.end_at) return 'Continue'
  const end = moment(med.end_at)
  if (!end.isValid()) return 'Continue'
  const start = med.start_at && moment(med.start_at).isValid() ? moment(med.start_at) : end
  const days = Math.max(1, Math.round(end.diff(start, 'days', true)))
  return `${days} day${days === 1 ? '' : 's'}`
}

const TEST_STATUS_TEXT = { pending: 'Pending', ready: 'Result ready' } as const

// "Paracetamol injection 600 mg — 1 ampoule as an injection into the muscle"; a procedure by name; a sample with the
// tests it was taken for. Deterministic, from facts only.
const clinicGivenText = (item: ClinicGivenItem): string => {
  const name = [item.name, item.kind === 'medicine' ? item.strength : null].filter(Boolean).join(' ').trim()
  if (item.kind === 'sample') return `Sample taken: ${name}`
  if (item.kind !== 'medicine') return name
  const dose = [item.dose_quantity, item.dose_unit].filter(v => v !== null && v !== undefined && v !== '').join(' ')
  const given = [dose, routeInPlainWords(item.route ?? null)].filter(Boolean).join(' ')
  return given ? `${name} — ${given}` : name
}

const formatAppointment = (appointment: VisitAppointment): string => {
  const date = appointment.date && moment(appointment.date).isValid() ? moment(appointment.date) : null
  const time =
    appointment.start_time && moment(appointment.start_time).isValid() ? moment(appointment.start_time) : null
  const when = [date?.format('ddd D MMM YYYY'), time?.format('h:mm a')].filter(Boolean).join(', ')
  const who = appointment.doctor_name ? `Dr ${appointment.doctor_name}` : null
  const tail = [who, appointment.purpose ? `(${appointment.purpose})` : null].filter(Boolean).join(' ')
  return [when, tail].filter(Boolean).join(' — ')
}

const formatDay = (value?: string | null): string | null =>
  value && moment(value).isValid() ? moment(value).format('D MMM YYYY') : null

// "Admitted 27 Sep 2026 · Discharged on 1 Oct 2026" — the discharge date once it is known: the doctor's chosen date
// (draft panel, completion snapshot) or, in live facts, the completed encounter's.
const hospitalStayLine = (stay?: HospitalStayFacts): string | null => {
  const admitted = formatDay(stay?.admitted_on)
  const discharged = formatDay(stay?.discharged_on)
  return (
    [admitted && `Admitted ${admitted}`, discharged && `Discharged on ${discharged}`].filter(Boolean).join(' · ') ||
    null
  )
}

// The translated page's own lines, in the fixed labels of its language — names, numbers and dates stay as they are.
const translatedPatientLine = (facts: VisitFacts, inpatient: boolean, language: DocumentLabelLanguage): string =>
  [
    facts.patient?.name,
    facts.patient?.age != null ? documentLabel(language, '{n} years', { n: facts.patient.age }) : null,
    facts.patient?.ref ? documentLabel(language, 'Hospital no. {ref}', { ref: facts.patient.ref }) : null,
    facts.doctor_name
      ? documentLabel(language, inpatient ? 'Doctor in charge: Dr {name}' : 'Seen by Dr {name}', {
          name: facts.doctor_name
        })
      : null,
    inpatient && facts.hospital_stay?.ward
      ? documentLabel(language, 'Ward: {ward}', { ward: facts.hospital_stay.ward })
      : null
  ]
    .filter(Boolean)
    .join(' · ')

const translatedStayLine = (stay: HospitalStayFacts | undefined, language: DocumentLabelLanguage): string | null => {
  const admitted = formatDay(stay?.admitted_on)
  const discharged = formatDay(stay?.discharged_on)
  return (
    [
      admitted && documentLabel(language, 'Admitted {date}', { date: admitted }),
      discharged && documentLabel(language, 'Discharged {date}', { date: discharged })
    ]
      .filter(Boolean)
      .join(' · ') || null
  )
}

/**
 * The printable / patient-portal visit summary: clinic letterhead (a slot — staff and portal
 * branding differ), the doctor-approved plain-language instructions, and the deterministic
 * medicine/test/appointment facts. Shared by the staff "Patient Summary" tab and the patient
 * portal's visit summary page (Design doc: docs/superpowers/specs/2026-09-27-patient-visit-summary-design.md).
 * For an admitted patient (facts.variant 'inpatient') the same page is the discharge instructions: hospital-stay
 * dates instead of the visit date, no tests list, appointments only when there are some
 * (docs/superpowers/specs/2026-10-01-inpatient-patient-instructions-design.md).
 */
const VisitSummaryDocument = ({
  facts,
  instructionsHtml,
  translationHtml,
  languageName,
  language,
  letterhead,
  clinicPhone,
  doctorNote
}: VisitSummaryDocumentProps) => {
  const inpatient = isInpatientFacts(facts)
  // Every facts section is left out when it has nothing in it (2026-10-02 end-of-visit spec).
  const medicines = facts.medication_changes ?? []
  const clinicGiven = inpatient ? [] : (facts.clinic_given ?? []).filter(item => item.name?.trim())
  const tests = inpatient ? [] : visitTests(facts)
  const appointments = facts.appointments ?? []
  const note = doctorNote?.trim() ?? ''
  // A summary completed before 2026-10-02 has no clinic_given: its table mixed clinic-given and take-home medicines.
  const legacyMedicines = !inpatient && facts.clinic_given === undefined
  const medicinesHeading = inpatient
    ? 'Your medicines from now on'
    : legacyMedicines
      ? 'Your medicines'
      : 'Medicines for home'
  const dateLine = inpatient ? hospitalStayLine(facts.hospital_stay) : formatDay(facts.visit_date)
  const doctorLabel = inpatient ? 'Doctor in charge: Dr' : 'Seen by Dr'

  const patientLine = [
    facts.patient?.name,
    facts.patient?.age != null ? `${facts.patient.age} years` : null,
    facts.patient?.ref ? `Hospital no. ${facts.patient.ref}` : null,
    facts.doctor_name ? `${doctorLabel} ${facts.doctor_name}` : null,
    inpatient && facts.hospital_stay?.ward ? `Ward: ${facts.hospital_stay.ward}` : null
  ]
    .filter(Boolean)
    .join(' · ')

  const showTranslation = Boolean(translationHtml?.trim() && languageName)
  // The translated page is labelled in its own language (fixed, reviewed labels — never machine-translated) when the
  // table has that language; otherwise it keeps the English labels.
  const local = documentLabelLanguage(language)
  const translationHeading = local
    ? documentLabel(local, 'Instructions in {language}', { language: documentLabel(local, '{language}') })
    : `Instructions in ${languageName}`
  const translationStayLine = local && inpatient ? translatedStayLine(facts.hospital_stay, local) : null
  const translationPatientLine = local ? translatedPatientLine(facts, inpatient, local) : patientLine

  return (
    <div className="visit-summary-document bg-white p-4 text-[13px] leading-relaxed text-[#333] print:p-0">
      <style>{visitSummaryDocumentCss}</style>

      <div className="flex flex-wrap items-start justify-between gap-3 border-b-2 border-[#0E7490] pb-2">
        <div className="min-w-0 flex-1">{letterhead}</div>
        <div className="shrink-0 text-right">
          <p className="text-sm font-bold">{inpatient ? 'YOUR DISCHARGE INSTRUCTIONS' : 'YOUR VISIT SUMMARY'}</p>
          {dateLine && <p>{dateLine}</p>}
        </div>
      </div>

      {patientLine && <p className="mt-2 text-[#333]">{patientLine}</p>}

      <div className="visit-summary-instructions print:break-inside-avoid mt-2 rounded-lg border border-[#9CC] bg-[#F0FAF8] p-3">
        <p className="text-sm font-bold">What you need to know</p>
        <div className="prose prose-sm max-w-none [&_p]:my-1.5 [&_ul]:my-1.5">
          {parse(sanitizeForPatient(instructionsHtml))}
        </div>
      </div>

      {clinicGiven.length > 0 && (
        <div className="mt-3">
          <p className="font-bold">Given in clinic</p>
          <ul className="list-disc pl-4">
            {clinicGiven.map(item => (
              <li key={item.key}>{clinicGivenText(item)}</li>
            ))}
          </ul>
        </div>
      )}

      {medicines.length > 0 && (
        <div className="mt-3">
          <p className="font-bold">{medicinesHeading}</p>
          <table className="mt-1 w-full border-collapse text-xs">
            <thead>
              <tr className="bg-[#f3f3f3]">
                <th className="p-1 text-left">Medicine</th>
                <th className="p-1 text-left">How to take it</th>
                <th className="p-1 text-left">For how long</th>
                <th className="p-1 text-center">Change</th>
              </tr>
            </thead>
            <tbody>
              {medicines.map(med => {
                const stopped = med.change === 'stopped'
                return (
                  <tr key={med.key}>
                    <td className={`p-1 align-top ${stopped ? 'text-[#888]' : ''}`}>
                      {stopped ? <s>{medicineName(med)}</s> : medicineName(med)}
                    </td>
                    <td className={`p-1 align-top ${stopped ? 'text-[#888]' : ''}`}>{howToTake(med)}</td>
                    <td className="p-1 align-top">{durationText(med)}</td>
                    <td className="p-1 text-center align-top">{CHANGE_BADGE[med.change]}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {(tests.length > 0 || appointments.length > 0) && (
        <div className="mt-3 flex flex-wrap gap-4">
          {tests.length > 0 && (
            <div className="min-w-[45%] flex-1">
              <p className="font-bold">Tests and results</p>
              <ul className="list-disc pl-4">
                {tests.map(test => (
                  <li key={test.name}>{test.status ? `${test.name} — ${TEST_STATUS_TEXT[test.status]}` : test.name}</li>
                ))}
              </ul>
            </div>
          )}
          {appointments.length > 0 && (
            <div className="min-w-[45%] flex-1">
              <p className="font-bold">Your appointments</p>
              <ul className="list-disc pl-4">
                {appointments.map((appointment, index) => (
                  // Appointment rows have no stable id in VisitFacts — index is fine, this list is
                  // never reordered or mutated in place.
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={index}>{formatAppointment(appointment)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {note && (
        <div className="visit-summary-doctor-note mt-3">
          <p className="font-bold">Note from your doctor</p>
          <p>
            {note.split('\n').map((line, index) => (
              // Lines of one note, never reordered.
              // eslint-disable-next-line react/no-array-index-key
              <span key={index}>
                {index > 0 && <br />}
                {line}
              </span>
            ))}
          </p>
        </div>
      )}

      {/* Completed documents: who completed it and when (from the snapshot facts); drafts have none. */}
      <SignatureBlock signature={facts.signature} />

      <div className="mt-3 border-t border-[#eee] pt-1.5 text-[11px] text-[#777]">
        <p>
          {inpatient
            ? 'Bring this paper and your medicines to your follow-up visit.'
            : 'Bring this paper and your medicines to your next visit.'}
        </p>
        {clinicPhone?.trim() && <p>Questions? Call the clinic on {clinicPhone.trim()}.</p>}
      </div>

      {showTranslation && (
        // Its own page when printed (break-before in visitSummaryDocumentCss), after the English page.
        <div className="visit-summary-translation mt-3 border-t border-dashed border-[#aaa] pt-2 print:border-t-0 print:pt-0">
          <p className="text-sm font-bold">{translationHeading}</p>
          {translationStayLine && <p className="text-[#333]">{translationStayLine}</p>}
          {translationPatientLine && <p className="text-[#333]">{translationPatientLine}</p>}
          <div className="prose prose-sm max-w-none [&_p]:my-1.5 [&_ul]:my-1.5">
            {parse(sanitizeForPatient(translationHtml as string))}
          </div>
          <SignatureBlock label={local ? documentLabel(local, 'Signed by:') : undefined} signature={facts.signature} />
        </div>
      )}
    </div>
  )
}

export default VisitSummaryDocument
